import React, { Suspense, lazy, useEffect } from 'react'

import { FormattedHTMLMessage, useBreakpoints } from '@web/walter-ui-kit'
import { StringResults, SwapTransaction } from '@web/walter-lib/api'
import { useLocation, useSearchParams } from 'react-router-dom'
import { logPosthogEvent } from '@walter/shared/utils/posthog'
import { rpPosthogEvents } from '@walter/shared/constants/posthog'
import { useWallet } from '@web/walter-lib/stream'
import styles from './style.module.css'
import SwapTransactionStatus from './SwapTransactionStatus'
import { lang } from './lang'
import { useData } from './useData'
import Preloader from './Preloader'
import { SwapPageLocationState } from './types'
import PageLayout from '~components/PageLayout'
import PageHeader from '~components/PageLayout/PageHeader'
import { QueryParams } from '~constants/routes'
import { useCurrencies, useGetWalletBalance } from '~api'

const SwapWidget = lazy(() => import('~components/SwapWidget'))

export interface SwapOrConvertTransaction extends SwapTransaction {
    convertPair?: StringResults['result']
}

export enum Steps {
    selectSwap,
    swapStatus,
}

function SwapPage() {
    const { data: currencies } = useCurrencies()
    const { isDesktop } = useBreakpoints()
    const location = useLocation()
    const locationState: SwapPageLocationState | undefined = location.state
    const { step, setStep, tx, setTx, swapTransaction, setSwapTransaction } = useData()
    const { data: walletSocketData } = useWallet()
    const { data: walletRequestData, isLoading: isWalletDataLoading } = useGetWalletBalance()
    const [searchParams] = useSearchParams()
    const toCurrency = searchParams.get(QueryParams.toCurrency) || ''
    const fromCurrency = searchParams.get(QueryParams.fromCurrency) || ''
    const amount = searchParams.get(QueryParams.amount) || ''

    const walletBalances = (() => {
        if (isWalletDataLoading) {
            return null
        }

        return walletSocketData || walletRequestData || {}
    })()

    useEffect(() => {
        logPosthogEvent(rpPosthogEvents.swapScreen)
    }, [])

    let header = null
    let content = null

    if (step === Steps.selectSwap) {
        header = (
            <PageHeader
                rootPage={isDesktop}
                withBackButton={locationState?.hasPreviousPage}
                title={<FormattedHTMLMessage {...lang.header} />}
            />
        )
        content = (
            <div className={styles.wrap}>
                <SwapWidget
                    amount={amount}
                    fromCurrency={fromCurrency}
                    toCurrency={toCurrency}
                    onSuccess={data => {
                        setSwapTransaction(data)
                        setStep(Steps.swapStatus)
                    }}
                    walletBalances={walletBalances}
                    isWalletDataLoading={isWalletDataLoading}
                />
            </div>
        )
    } else if (step === Steps.swapStatus && swapTransaction) {
        content = (
            <SwapTransactionStatus
                tx={tx}
                currencies={currencies}
                transaction={swapTransaction}
                onClose={() => {
                    setStep(Steps.selectSwap)
                    setTx(null)
                    setSwapTransaction(null)
                }}
            />
        )
    }

    return (
        <Suspense fallback={<Preloader />}>
            <PageLayout contentWidth="m" header={header}>
                {content}
            </PageLayout>
        </Suspense>
    )
}

export default SwapPage
